/**
 * Auth Context
 * Global authentication state management
 */

import { createContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { authService, LoginCredentials } from 'api/services';
import { setAccessToken, setRefreshToken, setUser, getUser, getAccessToken, clearAuth, isTokenExpired } from 'utils/token';
import { User } from 'models/user.model';

export interface RegisterPayload {
  username: string;
  password: string;
  firstName: string;
  lastName: string;
  email: string;
  cellphone: string;
}

/**
 * Auth context value interface
 */
export interface AuthContextValue {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (credentials: LoginCredentials) => Promise<{ success: boolean; user: User }>;
  requestOtp: (cellphone: string) => Promise<void>;
  register: (data: RegisterPayload) => Promise<{ success: boolean }>;
  logout: () => Promise<void>;
  updateUser: (updates: Partial<User>) => void;
  checkAuth: () => boolean;
}

/**
 * Auth provider props
 */
interface AuthProviderProps {
  children: ReactNode;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUserState] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  /**
   * Check authentication status
   */
  const checkAuth = useCallback((): boolean => {
    const savedUser = getUser();
    const token = getAccessToken();

    // Check if token exists and is not expired
    if (savedUser && token && !isTokenExpired(token)) {
      setUserState(savedUser);
      setIsAuthenticated(true);
      return true;
    } else {
      // Token is invalid or expired, clear auth
      clearAuth();
      setUserState(null);
      setIsAuthenticated(false);
      return false;
    }
  }, []);

  // Initialize auth state from localStorage and validate token
  useEffect(() => {
    const initAuth = (): void => {
      checkAuth();
      setIsLoading(false);
    };

    initAuth();
  }, [checkAuth]);

  // Periodically check token validity (every 5 minutes)
  useEffect(() => {
    const interval = setInterval(() => {
      const token = getAccessToken();
      if (token && isTokenExpired(token)) {
        // Token expired, logout user
        clearAuth();
        setUserState(null);
        setIsAuthenticated(false);
      }
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(interval);
  }, []);

  const extractErrorMessage = (error: unknown, fallback: string): string => {
    if (error && typeof error === 'object') {
      const axiosError = error as {
        response?: {
          data?: {
            detail?: string;
            errors?: string[];
            extensions?: { errors?: string[] };
            message?: string;
          };
        };
        message?: string;
      };
      const data = axiosError.response?.data;
      return (
        data?.errors?.[0] ??
        data?.extensions?.errors?.[0] ??
        data?.detail ??
        data?.message ??
        axiosError.message ??
        fallback
      );
    }
    return fallback;
  };

  const login = useCallback(async (credentials: LoginCredentials): Promise<{ success: boolean; user: User }> => {
    try {
      const { accessToken, refreshToken, user: userData } = await authService.login(credentials);

      setAccessToken(accessToken);
      setRefreshToken(refreshToken);
      setUser(userData);
      setUserState(userData);
      setIsAuthenticated(true);

      return { success: true, user: userData };
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Login failed'));
    }
  }, []);

  const requestOtp = useCallback(async (cellphone: string): Promise<void> => {
    try {
      await authService.requestOtp(cellphone);
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Failed to send OTP'));
    }
  }, []);

  const register = useCallback(async (data: RegisterPayload): Promise<{ success: boolean }> => {
    try {
      await authService.register(data);
      return { success: true };
    } catch (error) {
      throw new Error(extractErrorMessage(error, 'Registration failed'));
    }
  }, []);

  /**
   * Logout user
   */
  const logout = useCallback(async (): Promise<void> => {
    try {
      if (user?.id) {
        await authService.logout(user.id);
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      clearAuth();
      setUserState(null);
      setIsAuthenticated(false);
    }
  }, [user]);

  /**
   * Update user data
   */
  const updateUser = useCallback((updates: Partial<User>): void => {
    const updatedUser = { ...user, ...updates } as User;
    setUser(updatedUser);
    setUserState(updatedUser);
  }, [user]);

  const value: AuthContextValue = {
    user,
    isLoading,
    isAuthenticated,
    login,
    requestOtp,
    register,
    logout,
    updateUser,
    checkAuth
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export default AuthContext;
